import { Injectable } from '@angular/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { UserState, UserStateChecked } from '../../models';
import { RequestUserState, RequestUserStateSet } from '../../models/messages';
import { AdminService } from '../admin.service';

@Injectable({
  providedIn: 'root'
})

export class UserStateGridService extends BehaviorSubject<GridDataResult>  {

  /*-------------------------- public properties --------------------*/

  public loading: boolean = true;

  /*-------------------------- Internal properties  ------------------*/

  protected take: number = 5;

  /*-------------------------- Constructor  --------------------------*/

  constructor(protected lservice: AdminService) {

    const emptyDataResult = {
      data: [],
      total: 0
    }

    super(emptyDataResult);
  }

  /*-------------------------- public methods ------------------------*/

  query(req: RequestUserState): void {
    this.fetch(req)
      .subscribe((x) => super.next(x));
  }

  queryAll(): Observable<GridDataResult> {

    const req: RequestUserState = {

    };

    return this.fetch(req);
  }

  fetch(req: RequestUserState): Observable<GridDataResult> {

    this.take = req.take ?? 0;

    const items: UserStateChecked[] = [];

    return this.lservice.getUserState$(req)
      .pipe(
        map(
          (response) =>
            <GridDataResult>{
              data: response.data!.map(src => {
                const dest = new UserStateChecked();
                Object.assign(dest, src);
                dest.checked = null;
                return dest;
              }
              )
              , total: response.total
            }
        )
        , finalize(() => (this.loading = false))
      );
  }

  save(data: UserState[]): Observable<boolean> {

    return this.saveApi(data)
      .pipe(
        map(modifiedData => {
          return this.syncModificationsToLocal(modifiedData);
        })
        , tap(x => { super.next(x); })
        , map(() => true)
        , catchError(() => of(false))
      );
  }

  /*--------------------------- internal functions  ------------------*/

  protected saveApi(data: UserState[]): Observable<UserState[]> {

    let req: RequestUserStateSet = new RequestUserStateSet();
    req.data = data;

    return this.lservice.setUserState$(req)
      .pipe(
        map(response => response.data!)
        , finalize(() => (this.loading = false))
      );

  }

  protected syncModificationsToLocal(modifiedItems: UserState[]): GridDataResult {

    let totalCount = this.value.total;

    let pos: number = 0;
    let item: UserState = {};
    let itemMod: UserState = {};

    let newData: UserState[] = [];

    // Create a copy of our array to manipulate and return
    for (pos = 0; pos < this.value.data.length; pos++) {
      newData.push(this.value.data[pos]);
    }

    // Get each of the modified items
    for (let j = modifiedItems.length - 1; j >= 0; j--) {

      itemMod = modifiedItems[j];
      let found = false;

      // See if they exist in our data already
      for (let i = newData.length - 1; i >= 0; i--) {

        item = newData[i];

        if (item.id == itemMod.id) {

          // match was found so substitute the modification
          newData.splice(i, 1, itemMod);
          found = true;
          break;
        }
      }

      if (found == false) {
        // Not found so insert the new item at the top of the list - user can see addition then
        newData.splice(0, 0, itemMod);
        totalCount += 1;

        // Remove items from the end of the array if we now overlap the page size due to the addition
        if (newData.length > this.take)
          newData.splice(this.take, 1);
      }
    }

    // Return the updated data array
    return { data: newData, total: totalCount };
  }

}
